import React, { useContext, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    AccountType,
    PensionContext,
    RecurringPaymentInterval,
} from '../../../../contexts/pension-robot/PensionContext';
import { toPrice } from '../../../../utils/parsers';
import { OtherIncome } from '../../../../contexts/pension-robot/PensionContext';
import { Button } from '../../../general/Button';
import { useGetLinkInvestmentFree } from '../../../../api/pension-robot/useGetLinkInvestmentFree';
import { DateTime } from 'luxon';
import { useGetLinkSavingsRetirement } from '../../../../api/pension-robot/useGetLinkSavingsRetirement';
import { useGetLinkInvestmentRetirement } from '../../../../api/pension-robot/useGetLinkInvestmentRetirement';
import { isGoalAchieved, isPensionCalculatedTogether } from '../../../../utils/functions';
import { DEFAULT_PENSION_AGE, LAST_SECTION_INDEX, riskProfiles } from '../../../../config/constants';
import { useGetPensionCalcData } from '../../../../api/pension-robot/useGetPensionCalcData';
import { useNetCalculation } from '../../../../hooks/netGrossCalculations';
import { BNDListArrows } from '../LegalSection';

export const SummaryDisclosure: React.FC<{ isForPrinting?: boolean }> = ({ isForPrinting = false }) => {
    const {
        parameters: { personalInfo, expensesInfo, supplementInfo },
    } = useContext(PensionContext);
    const { t } = useTranslation('pension');
    const pensionCalcData = useGetPensionCalcData();
    const legalPensionAge = pensionCalcData.data?.legalRetirementAge ?? DEFAULT_PENSION_AGE;
    const pensionAgeDelta = !!personalInfo.self?.pensionAge ? personalInfo.self.pensionAge - legalPensionAge : 0;
    const pensionAgeDeltaPartner = !!personalInfo.partner?.pensionAge
        ? personalInfo.partner.pensionAge - legalPensionAge
        : 0;
    const pensionDate = DateTime.now().plus({ years: Math.max(legalPensionAge - (personalInfo.self?.age ?? 0), 0) });

    const renderYearSpaceAccount =
        (!!supplementInfo?.yearSpacePensionAccount?.monthlyPaymentAmount
            ? supplementInfo.yearSpacePensionAccount.monthlyPaymentAmount > 0
            : false) && !!supplementInfo?.yearSpacePensionAccount;
    const renderAdditionalSavingsAccount = !!supplementInfo?.self?.additionalSavingAccount?.isUsed;
    const renderAdditionalInvestmentAccount = !!supplementInfo?.self?.additionalInvestmentAccount?.isUsed;
    const renderGift = !!supplementInfo?.otherIncome?.gift ? supplementInfo.otherIncome.gift > 0 : false;
    const renderHeritage = !!supplementInfo?.otherIncome?.heritage ? supplementInfo.otherIncome.heritage > 0 : false;
    const renderSurplusHouse = !!supplementInfo?.otherIncome?.surplusHouse
        ? supplementInfo.otherIncome.surplusHouse > 0
        : false;
    const renderOther = !!supplementInfo?.otherIncome?.other ? supplementInfo.otherIncome.other > 0 : false;
    const renderOtherIncome =
        !!supplementInfo?.otherIncomeAvailable && (renderGift || renderHeritage || renderSurplusHouse || renderOther);
    const renderWithPartner = isPensionCalculatedTogether(personalInfo);
    return (
        <div className={`flex flex-col gap-4 p-4 pb-8 ${isForPrinting ? '' : 'border-b'}`}>
            {
                <div>{`${t('pension-result-screen-summary-introduction1')}${
                    isForPrinting ? '' : t('pension-result-screen-summary-introduction2')
                }`}</div>
            }
            {renderWithPartner && t('pension-result-screen-summary-overage-shortage-partner-prefix')}
            <ShortageOverageSummary goal={expensesInfo?.goal ?? 0} renderWithPartner={renderWithPartner} />
            <div>{t('pension-result-screen-summary-based-on')}</div>
            {(pensionAgeDelta !== 0 || (pensionAgeDeltaPartner !== 0 && renderWithPartner)) && (
                <BNDListArrows>
                    {pensionAgeDelta !== 0 && (
                        <PensionAge pensionAgeDelta={pensionAgeDelta} legalPensionAge={legalPensionAge} />
                    )}
                    {pensionAgeDeltaPartner !== 0 && renderWithPartner && (
                        <PensionAgePartner
                            pensionAgeDeltaPartner={pensionAgeDeltaPartner}
                            legalPensionAge={legalPensionAge}
                        />
                    )}
                </BNDListArrows>
            )}
            <div>
                <Trans
                    t={t}
                    i18nKey={
                        renderWithPartner
                            ? 'pension-result-screen-summary-net-monthly-partner'
                            : 'pension-result-screen-summary-net-monthly'
                    }
                    values={{
                        value: toPrice(expensesInfo?.goal ?? 0, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                    }}
                >
                    ...
                    <span className="font-medium">value</span>
                    ...
                </Trans>
            </div>
            {(renderYearSpaceAccount || renderAdditionalInvestmentAccount || renderAdditionalSavingsAccount) && (
                <BNDListArrows>
                    {renderYearSpaceAccount && (
                        <YearspaceAccount
                            accountType={
                                supplementInfo?.yearSpacePensionAccount?.typeOfAdditionalPensionAccount ?? 'saving'
                            }
                            amount={supplementInfo?.yearSpacePensionAccount?.monthlyPaymentAmount ?? 0}
                            riskId={supplementInfo?.yearSpacePensionAccount?.riskProfileId ?? riskProfiles.neutral}
                            pensionDate={pensionDate.toISODate()}
                            isForPrinting={isForPrinting}
                        />
                    )}
                    {renderAdditionalSavingsAccount && (
                        <AdditionalSavingAccount
                            amountStart={supplementInfo?.self?.additionalSavingAccount?.account.currentAmount ?? 0}
                            period={
                                supplementInfo?.self?.additionalSavingAccount?.account.recurringPaymentInterval ??
                                'month'
                            }
                            amountPeriod={
                                supplementInfo?.self?.additionalSavingAccount?.account.recurringPaymentAmount ?? 0
                            }
                        />
                    )}
                    {renderAdditionalInvestmentAccount && (
                        <AdditionalInvestmentAccount
                            amountStart={supplementInfo?.self?.additionalInvestmentAccount?.account.currentAmount ?? 0}
                            period={
                                supplementInfo?.self?.additionalInvestmentAccount?.account.recurringPaymentInterval ??
                                'month'
                            }
                            amountPeriod={
                                supplementInfo?.self?.additionalInvestmentAccount?.account.recurringPaymentAmount ?? 0
                            }
                            riskId={
                                supplementInfo?.self?.additionalInvestmentAccount?.account.riskProfile ??
                                riskProfiles.neutral
                            }
                            investmentEndDate={pensionDate.toISODate()}
                            isForPrinting={isForPrinting}
                        />
                    )}
                </BNDListArrows>
            )}
            {renderOtherIncome && <div>{t('pension-result-screen-summary-other-income')}</div>}
            {renderOtherIncome && (
                <BNDListArrows>
                    {renderGift && (
                        <OtherIncomeText otherIncome={'gift'} amount={supplementInfo?.otherIncome?.gift ?? 0} />
                    )}
                    {renderHeritage && (
                        <OtherIncomeText otherIncome={'heritage'} amount={supplementInfo?.otherIncome?.heritage ?? 0} />
                    )}
                    {renderSurplusHouse && (
                        <OtherIncomeText
                            otherIncome={'surplusHouse'}
                            amount={supplementInfo?.otherIncome?.surplusHouse ?? 0}
                        />
                    )}
                    {renderOther && (
                        <OtherIncomeText otherIncome={'other'} amount={supplementInfo?.otherIncome?.other ?? 0} />
                    )}
                </BNDListArrows>
            )}
        </div>
    );
};

const ShortageOverageSummary: React.FC<{
    goal: number;
    renderWithPartner: boolean;
}> = ({ goal, renderWithPartner }) => {
    const netValuesAtRetirementAgeOfSelf = useNetCalculation(LAST_SECTION_INDEX, false);
    const netValuesAtRetirementAgeOfPartner = useNetCalculation(LAST_SECTION_INDEX, true);

    const netIncomeAtAgeOfSelfRetirement = netValuesAtRetirementAgeOfSelf.netIncome.total.monthly;
    const netIncomeAtAgeOfPartnerRetirement = netValuesAtRetirementAgeOfPartner.netIncome.total.monthly;

    const selfGoalVariables = isGoalAchieved(goal, netIncomeAtAgeOfSelfRetirement);
    const partnerGoalVariables = isGoalAchieved(goal, netIncomeAtAgeOfPartnerRetirement);

    const SelfOverageOrShortage = () => {
        return (
            <OverageShortage
                renderAsPartner={false}
                renderTogether={renderWithPartner}
                goalAchieved={selfGoalVariables.achieved}
                difference={selfGoalVariables.difference}
            />
        );
    };

    const PartnerOverageOrShortage = () => {
        return (
            <OverageShortage
                renderAsPartner={true}
                renderTogether={true}
                goalAchieved={partnerGoalVariables.achieved}
                difference={partnerGoalVariables.difference}
            />
        );
    };

    return (
        <BNDListArrows>
            <SelfOverageOrShortage />
            {renderWithPartner && <PartnerOverageOrShortage />}
        </BNDListArrows>
    );
};

const OverageShortage: React.FC<{
    renderAsPartner: boolean;
    goalAchieved: boolean;
    difference: number;
    renderTogether: boolean;
}> = ({ renderAsPartner, renderTogether, goalAchieved, difference }) => {
    const { t } = useTranslation('pension');

    const getTextId = () => {
        return renderTogether
            ? renderAsPartner
                ? 'pension-result-screen-summary-overage-shortage-partner-2'
                : 'pension-result-screen-summary-overage-shortage-partner-1'
            : 'pension-result-screen-summary-overage-shortage';
    };

    return (
        <div>
            <Trans
                t={t}
                i18nKey={getTextId()}
                values={{
                    overageOrShortage: getOverageOrShortage(goalAchieved),
                    value: toPrice(difference, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    }),
                }}
            >
                ...
                <span className="font-medium">overageOrShortage</span>
                <span className="font-medium">value</span>
                ...
            </Trans>
        </div>
    );
};

const PensionAge: React.FC<{ pensionAgeDelta: number; legalPensionAge: number }> = ({
    pensionAgeDelta,
    legalPensionAge,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    return (
        <div>
            <Trans
                t={t}
                i18nKey="pension-result-screen-summary-pension-age-altered"
                values={{
                    increaseOrDecrease: getPensionAgeAltered(pensionAgeDelta),
                    age: personalInfo.self?.pensionAge ?? legalPensionAge,
                }}
            >
                ...
                <span className="font-medium">increaseOrDecrease</span>
                <span className="font-medium">age</span>
                ...
            </Trans>
        </div>
    );
};

const PensionAgePartner: React.FC<{ pensionAgeDeltaPartner: number; legalPensionAge: number }> = ({
    pensionAgeDeltaPartner,
    legalPensionAge,
}) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    return (
        <div>
            <Trans
                t={t}
                i18nKey="pension-result-screen-summary-pension-age-altered-partner"
                values={{
                    increaseOrDecrease: getPensionAgeAltered(pensionAgeDeltaPartner),
                    age: personalInfo.partner?.pensionAge ?? legalPensionAge,
                }}
            >
                ...
                <span className="font-medium">increaseOrDecrease</span>
                <span className="font-medium">age</span>
                ...
            </Trans>
        </div>
    );
};

const YearspaceAccount: React.FC<{
    accountType: AccountType;
    amount: number;
    riskId: string;
    pensionDate: string;
    isForPrinting: boolean;
}> = ({ accountType, amount, riskId, pensionDate, isForPrinting = false }) => {
    const { t } = useTranslation('pension');

    const getSavingsLinkQuery = useGetLinkSavingsRetirement(
        {
            paymentSingleAmount: 0,
            paymentRecurringAmount: amount,
            paymentRecurringInterval: 'Month',
            investmentEndDate: pensionDate,
            paymentRecurringEndDate: pensionDate,
            monthOfPayment: 0,
        },
        false,
    );
    const getInvestmentLinkQuery = useGetLinkInvestmentRetirement(
        {
            paymentSingleAmount: 0,
            paymentRecurringAmount: amount,
            paymentRecurringInterval: 'Month',
            fundModelTypeId: Number.parseInt(riskId),
            investmentEndDate: pensionDate,
            paymentRecurringEndDate: pensionDate,
            monthOfPayment: 0,
            riskReduction: true,
        },
        false,
    );

    useEffect(() => {
        if (getInvestmentLinkQuery.data) {
            const newTab = window.open('', '_blank');
            newTab && (newTab.location.href = getInvestmentLinkQuery.data);
        }
    }, [getInvestmentLinkQuery.data]);

    useEffect(() => {
        if (getSavingsLinkQuery.data) {
            const newTab = window.open('', '_blank');
            newTab && (newTab.location.href = getSavingsLinkQuery.data);
        }
    }, [getSavingsLinkQuery.data]);

    return (
        <div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-4">
            <div>
                <Trans
                    t={t}
                    i18nKey={
                        accountType === 'saving'
                            ? 'pension-result-screen-summary-yearspace-savings'
                            : 'pension-result-screen-summary-yearspace-investment'
                    }
                    values={{
                        valuePeriod: toPrice(amount * 100, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                    }}
                >
                    ...
                    <span className="font-medium">valuePeriod</span>
                    ...
                </Trans>
            </div>
            <div>
                {!isForPrinting && (
                    <Button
                        className={`w-48 px-4`}
                        buttonType="primary"
                        onClick={() => {
                            accountType === 'saving' ? getSavingsLinkQuery.refetch() : getInvestmentLinkQuery.refetch();
                        }}
                    >
                        {t('general-open-account')}
                    </Button>
                )}
            </div>
        </div>
    );
};

const AdditionalSavingAccount: React.FC<{
    amountStart: number;
    period: RecurringPaymentInterval;
    amountPeriod: number;
}> = ({ amountStart, period, amountPeriod }) => {
    const { t } = useTranslation('pension');

    return (
        <div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-4">
            <div>
                <Trans
                    t={t}
                    i18nKey="pension-result-screen-summary-savings"
                    values={{
                        valueStart: toPrice(amountStart, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                        period: getPeriodText(period),
                        valuePeriod: toPrice(amountPeriod, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                    }}
                >
                    ...
                    <span className="font-medium">valueStart</span>
                    <span className="font-medium">period</span>
                    <span className="font-medium">valuePeriod</span>
                    ...
                </Trans>
            </div>
        </div>
    );
};

const AdditionalInvestmentAccount: React.FC<{
    amountStart: number;
    period: RecurringPaymentInterval;
    amountPeriod: number;
    riskId: string;
    investmentEndDate: string;
    isForPrinting: boolean;
}> = ({ amountStart, period, amountPeriod, riskId, investmentEndDate, isForPrinting = false }) => {
    const { t } = useTranslation('pension');

    const getLinkQuery = useGetLinkInvestmentFree(
        {
            paymentSingleAmount: amountStart / 100,
            paymentRecurringAmount: amountPeriod / 100,
            paymentRecurringInterval: period,
            fundModelTypeId: Number.parseInt(riskId),
            investmentEndDate: investmentEndDate,
            riskReduction: true,
        },
        false,
    );

    useEffect(() => {
        if (getLinkQuery.data) {
            const newTab = window.open('', '_blank');
            newTab && (newTab.location.href = getLinkQuery.data);
        }
    }, [getLinkQuery.data]);

    return (
        <div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-4">
            <div>
                <Trans
                    t={t}
                    i18nKey="pension-result-screen-summary-investment"
                    values={{
                        valueStart: toPrice(amountStart, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                        period: getPeriodText(period),
                        valuePeriod: toPrice(amountPeriod, {
                            onlyFullEuros: true,
                            withCurrencySymbol: true,
                        }),
                    }}
                >
                    ...
                    <span className="font-medium">valueStart</span>
                    <span className="font-medium">period</span>
                    <span className="font-medium">valuePeriod</span>
                    ...
                </Trans>
            </div>
            <div>
                {!isForPrinting && (
                    <Button
                        className={`w-48 px-4`}
                        buttonType="primary"
                        onClick={() => {
                            getLinkQuery.refetch();
                        }}
                    >
                        {t('general-open-account')}
                    </Button>
                )}
            </div>
        </div>
    );
};

const OtherIncomeText: React.FC<{ otherIncome: OtherIncome; amount: number }> = ({ otherIncome, amount }) => {
    const { t } = useTranslation('pension');

    return (
        <div>
            <Trans
                t={t}
                i18nKey={
                    otherIncome === 'gift'
                        ? 'pension-result-screen-summary-gift'
                        : otherIncome === 'heritage'
                        ? 'pension-result-screen-summary-heritage'
                        : otherIncome === 'surplusHouse'
                        ? 'pension-result-screen-summary-surplus'
                        : 'pension-result-screen-summary-other'
                }
                values={{
                    value: toPrice(amount, {
                        onlyFullEuros: true,
                        withCurrencySymbol: true,
                    }),
                }}
            >
                ...
                <span className="font-medium">value</span>
                ...
            </Trans>
        </div>
    );
};

const getOverageOrShortage = (isGoalAchieved: boolean) => (isGoalAchieved ? 'overschot' : 'tekort');

const getPensionAgeAltered = (pensionAgeDelta: number) => (pensionAgeDelta < 0 ? 'verlaagd' : 'verhoogd');

const getPeriodText = (period: RecurringPaymentInterval) => (period === 'month' ? 'maandelijks' : 'jaarlijks');
